/**
 * B8 生产部署核对 — 管理员总览上的「系统状态」卡 (docs/DEPLOY.md).
 *
 * Answers the four questions an operator actually has: 能不能写库、对象存储通不通、
 * 盘还剩多少、上一次备份是什么时候。`GET health/deep` and `GET admin/backups`
 * are both admin-only, so the whole card renders for administrators only —
 * anyone else does not see a 403, they simply do not see the card.
 *
 * Status never depends on colour alone: every row has an icon, a 中文 verdict
 * and the raw number (DESIGN_SYSTEM.md 视觉规则).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Icon, useConfirmSafe, useToast } from "@xinghai/ui";
import type { IconId } from "@xinghai/ui";
import { opsApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { ErrorNotice, LoadingBlock, Section, StatusTag, formatDateTime } from "./common";
import type { BackupStatus, HealthCheckStatus } from "./types";

const STATUS_META: Record<HealthCheckStatus, { icon: IconId; label: string }> = {
  ok: { icon: "ui-check-circle", label: "正常" },
  warn: { icon: "ui-warning", label: "注意" },
  fail: { icon: "ui-error", label: "故障" },
  skipped: { icon: "ui-info", label: "未启用" },
};

const CHECK_LABEL: Record<string, string> = {
  db: "数据库",
  storage: "对象存储",
  disk: "磁盘",
  backup: "备份",
  smtp: "发信",
};

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

/** One 中文 sentence describing the backup schedule. */
export function backupSchedule(b: BackupStatus): string {
  if (!b.enabled) return "定时备份没有开启（设置 XZ_BACKUP=on）。";
  return `每天 ${String(b.hour).padStart(2, "0")}:00（北京时间）备份一次，保留最近 ${b.keep} 份${
    b.uploads ? "，并同步到对象存储" : ""
  }。`;
}

export function SystemStatusCard() {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();

  const health = useQuery({
    queryKey: qk.healthDeep,
    queryFn: ({ signal }) => opsApi.healthDeep(signal),
    retry: false,
  });
  const backups = useQuery({
    queryKey: qk.adminBackups,
    queryFn: ({ signal }) => opsApi.backups(signal),
    retry: false,
  });

  const runBackup = useMutation({
    mutationFn: () => opsApi.runBackup(),
    onSuccess: (data) => {
      // the endpoint is always 200 — the outcome lives in `result.ok`
      if (data.result.ok) {
        toast.success(
          `已备份：${data.result.file}（${formatBytes(data.result.size_bytes)}）` +
            (data.result.uploaded_key ? "，已同步到对象存储" : ""),
        );
      } else {
        toast.error(`备份没有成功：${data.result.error ?? "未知原因"}`);
      }
      void qc.invalidateQueries({ queryKey: qk.adminBackups });
      void qc.invalidateQueries({ queryKey: qk.healthDeep });
    },
    onError: (err) => toast.error(friendlyMessage(err, "备份没能执行，请稍后再试。")),
  });

  const askBackup = async () => {
    const ok = await confirm({
      title: "立即备份一次？",
      message: "会把当前数据库完整快照一份，存到备份目录，并按保留策略清理旧的。",
      confirmLabel: "立即备份",
    });
    if (ok) runBackup.mutate();
  };

  if (health.isPending || backups.isPending) {
    return (
      <Section title="系统状态" subtitle="数据库、存储、磁盘与备份">
        <LoadingBlock rows={2} label="正在检查系统状态" />
      </Section>
    );
  }

  if (health.isError || backups.isError) {
    return (
      <Section title="系统状态" subtitle="数据库、存储、磁盘与备份">
        <ErrorNotice
          error={health.error ?? backups.error}
          onRetry={() => {
            void health.refetch();
            void backups.refetch();
          }}
          title="系统状态没能加载"
        />
      </Section>
    );
  }

  const h = health.data;
  const b = backups.data.backup;
  const overall = STATUS_META[h.status];

  return (
    <Section
      title="系统状态"
      subtitle={backupSchedule(b)}
      actions={
        <Button
          leadingIcon={<Icon id="ui-download" size={16} />}
          loading={runBackup.isPending}
          onClick={() => void askBackup()}
        >
          立即备份
        </Button>
      }
    >
      <div className="xz-ops">
        <p className="xz-adm__rowactions">
          <StatusTag
            icon={overall.icon}
            tone={h.status === "ok" ? "success" : h.status === "fail" ? "danger" : "warning"}
          >
            总体 {overall.label}
          </StatusTag>
          <StatusTag icon="ui-clock" tone={b.age_hours === null ? "warning" : "neutral"}>
            {b.last
              ? `上次备份 ${formatDateTime(b.last.at)} · ${formatBytes(b.last.size_bytes)}`
              : "还没有备份"}
          </StatusTag>
          <StatusTag icon="ui-inbox">保留 {backups.data.backups.length} 份</StatusTag>
        </p>

        <ul className="xz-ops__checks" aria-label="健康检查">
          {h.checks.map((check) => {
            const meta = STATUS_META[check.status];
            return (
              <li key={check.name} className="xz-ops__check" data-status={check.status}>
                <Icon id={meta.icon} size={20} />
                <span className="xz-ops__checktext">
                  <span className="xz-ops__checkname">
                    {CHECK_LABEL[check.name] ?? check.name} · {meta.label}
                  </span>
                  <span className="gz-caption">{check.detail}</span>
                </span>
              </li>
            );
          })}
        </ul>

        <p className="xz-ops__meta">
          <span>版本 {h.version}</span>
          {h.commit && <span>commit {h.commit.slice(0, 8)}</span>}
          <span>Node {h.node}</span>
          <span>存储 {h.storage.kind}</span>
          <span>回避策略 {h.avoidance}</span>
          <span>已运行 {Math.round(h.uptime_s / 3600)} 小时</span>
        </p>
      </div>
    </Section>
  );
}
